import _ from 'underscore';

import {
  DataTableColumn,
  DataTableConfig,
  FromServerDataTableColumn,
  FromServerDataTableConfig,
} from 'notebook/data-tables/DataTableConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {getPrimaryLinkField} from 'results/PrimaryLinks';
import {FIELD_TYPES, FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getFieldForColumn} from 'schemas/fields/SchemaFieldGetters';
import {getFieldTypeName} from 'tag-schema/util/TagSchemaUtil';
import uniqueId from 'util/uniqueId';

// Agent keep in sync: ASSAY_FIELD_TYPES in benchling/schemas/validation/assay_field_types.py
export const ALL_SUPPORTED_FIELD_TYPES = [
  // FIELD_TYPES.ASSAY_REQUEST_LINK.type, - Not supported by rendering UI yet
  FIELD_TYPES.ASSAY_RESULT_LINK.type,
  FIELD_TYPES.ASSAY_RUN_LINK.type,
  FIELD_TYPES.BATCH_LINK.type,
  FIELD_TYPES.BLOB_LINK.type,
  FIELD_TYPES.BOOLEAN.type,
  FIELD_TYPES.DATE.type,
  FIELD_TYPES.DATETIME.type,
  FIELD_TYPES.ENTRY_LINK.type,
  FIELD_TYPES.BIOENTITY_LINK.type,
  FIELD_TYPES.FLOAT.type,
  FIELD_TYPES.INTEGER.type,
  // FIELD_TYPES.JSON.type, - Not supported by rendering UI yet
  FIELD_TYPES.LONG_TEXT.type,
  // FIELD_TYPES.SELECTOR.type, - Not supported by SetDataTableColumnTypeModal yet
  FIELD_TYPES.STORABLE_LINK.type,
  FIELD_TYPES.TEXT.type,
];

// Agent keep in sync: ASSAY_FIELD_TYPES_FOR_MANUAL_ENTRY in benchling/schemas/validation/assay_field_types.py
const FIELD_TYPES_FOR_MANUAL_ENTRY = [
  FIELD_TYPES.BATCH_LINK.type,
  FIELD_TYPES.BLOB_LINK.type,
  FIELD_TYPES.BOOLEAN.type,
  FIELD_TYPES.DATE.type,
  FIELD_TYPES.DATETIME.type,
  FIELD_TYPES.ENTRY_LINK.type,
  FIELD_TYPES.BIOENTITY_LINK.type,
  FIELD_TYPES.FLOAT.type,
  FIELD_TYPES.INTEGER.type,
  FIELD_TYPES.LONG_TEXT.type,
  FIELD_TYPES.SELECTOR.type,
  FIELD_TYPES.STORABLE_LINK.type,
  FIELD_TYPES.TEXT.type,
];
// TODO(saif): Pull into util module and share with NoteUtil
function _addCidIfMissing(
  item: {id: string | number; cid?: string | null},
  prefix: string,
  cidIdMap: CidIdMap
): void {
  if (item.cid != null) {
    return;
  }
  item.cid = uniqueId(prefix);
  cidIdMap[item.cid] = item.id;
  // @ts-expect-error: id is required in the paramater, but removed for output
  delete item.id;
}

export function prepareDataTableConfigForStore(
  serverDataTableConfig: FromServerDataTableConfig,
  cidIdMap: CidIdMap
): void {
  _addCidIfMissing(serverDataTableConfig, 'dtc', cidIdMap);
  serverDataTableConfig.columns.forEach((column) => {
    _addCidIfMissing(column, 'dtcol', cidIdMap);
  });
}

export function isFieldTypeSupportedForManualEntry(fieldType: FieldType): boolean {
  return FIELD_TYPES_FOR_MANUAL_ENTRY.includes(fieldType);
}

export function isDataTableConfigValidForManualEntry(
  dataTableConfig: DataTableConfig | FromServerDataTableConfig
): boolean {
  const {assaySchema, columns} = dataTableConfig;
  return assaySchema.fields
    .filter((field) =>
      columns.some(
        ({tagSchemaFieldId}: DataTableColumn | FromServerDataTableColumn) => tagSchemaFieldId === field.id
      )
    )
    .every((field) => isFieldTypeSupportedForManualEntry(field.fieldType));
}

export function getColumnTypeFromSchema(dataTableColumn: DataTableColumn, assaySchema: AssaySchema): string {
  if (dataTableColumn.tagSchemaFieldId == null) {
    return FIELD_TYPES.TEXT.type;
  }
  return getFieldForColumn(assaySchema, dataTableColumn)!.fieldType;
}

export function getUnsupportedFieldTypeText(fieldName: string, fieldType: string): string {
  const name = `"${fieldName}" of type ${getFieldTypeName(fieldType)}`;
  return `Field ${name} is not supported for manual data entry.`;
}

export function getFieldTypeWarnings(
  dataTableConfig: DataTableConfig,
  columnTypesByCid: {[cid: string]: FieldType},
  assaySchema: AssaySchema
): Array<string> {
  const warnings = [];

  for (const column of dataTableConfig.columns) {
    let fieldType = columnTypesByCid[column.cid];
    if (fieldType == null) {
      const field = getFieldForColumn(assaySchema, column);
      fieldType = field == null ? FIELD_TYPES.TEXT.type : field.fieldType;
    }

    if (!isFieldTypeSupportedForManualEntry(fieldType)) {
      warnings.push(getUnsupportedFieldTypeText(column.name, fieldType));
    }
  }
  return warnings;
}

export function getUnusedSchemaFields(
  assaySchema: AssaySchema,
  columns: Array<DataTableColumn>
): Array<SchemaField> {
  return assaySchema.fields.filter(
    (field) =>
      !columns.some(({tagSchemaFieldId}) => tagSchemaFieldId === field.id) &&
      field !== getPrimaryLinkField(assaySchema)
  );
}

export function getUnusedSchemaFieldIds(
  assaySchema: AssaySchema,
  columns: Array<DataTableColumn>
): Array<number> {
  // eslint-disable-next-line no-restricted-syntax
  return _.pluck(getUnusedSchemaFields(assaySchema, columns), 'id');
}

export function getFieldsToDelete(
  assaySchema: AssaySchema,
  columns: Array<DataTableColumn>,
  hiddenFieldIds: Array<number>
): Array<SchemaField> {
  const unusedSchemaFieldIds = getUnusedSchemaFieldIds(assaySchema, columns);

  return assaySchema.fields.filter(
    (field) => unusedSchemaFieldIds.includes(field.id) && !hiddenFieldIds.includes(field.id)
  );
}
